import { Chip } from '@mui/material';
import {
  CreateButton,
  Datagrid,
  FunctionField,
  List,
  TextField,
  TopToolbar,
  useRecordContext,
} from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import type { CannedResponse } from '../dataProvider';
import { CANNED_KIND_COLOR, CANNED_KIND_LABEL } from './cannedResponsesState';

/**
 * Canned response library management (D-canned-responses, the Phase 6 surface-2 library).
 * Charities-shaped managed CRUD: list / create / edit, with a DEACTIVATE-not-delete model
 * (the active flag retires a template; there is no delete anywhere). A retired template
 * stays referenceable in history and leaves the agents' use library.
 *
 * The whole management surface is gated `canned_responses:write` (super_admin via '*'):
 * managing the shared copy reaches every member at once, so it is higher-trust than USING
 * a template (which the support agent does from a ticket via `canned_responses:read`).
 *
 * GLOBAL, not region-scoped: the library is shared across uk/us in v1 (no market tag), so
 * there is no switcher filter here. Audited for free by the provider wrapper.
 */

/** Active/inactive as a chip — the soft-disable state. */
const ActiveChip = () => {
  const record = useRecordContext<CannedResponse>();
  const active = record?.active === true;
  return (
    <Chip
      size="small"
      label={active ? 'Active' : 'Inactive'}
      color={active ? 'success' : 'default'}
      variant={active ? 'filled' : 'outlined'}
    />
  );
};

const KindChip = () => {
  const record = useRecordContext<CannedResponse>();
  if (!record) return null;
  return (
    <Chip
      size="small"
      variant="outlined"
      color={CANNED_KIND_COLOR[record.kind]}
      label={CANNED_KIND_LABEL[record.kind]}
    />
  );
};

const CannedResponseListActions = () => (
  <TopToolbar>
    <CreateButton />
  </TopToolbar>
);

/** The list itself. Exported for testing without the access wrapper. */
export const CannedResponseListView = () => (
  <List
    resource="cannedResponses"
    title="Canned responses"
    actions={<CannedResponseListActions />}
    sort={{ field: 'name', order: 'ASC' }}
    perPage={25}
    exporter={false}
  >
    {/* Clicking a row opens the editor. No bulk actions, and no bulk delete: a
        template is retired via its active flag, never deleted. */}
    <Datagrid rowClick="edit" bulkActionButtons={false}>
      <TextField source="name" label="Name" />
      <FunctionField label="Kind" sortBy="kind" render={() => <KindChip />} />
      <FunctionField
        label="Status"
        sortBy="active"
        render={() => <ActiveChip />}
      />
    </Datagrid>
  </List>
);

/** Gated entry point registered as the `cannedResponses` Resource's list. */
export const CannedResponseList = () => (
  <RequireAccess capability="canned_responses:write">
    <CannedResponseListView />
  </RequireAccess>
);
